import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { useSEO } from '../hooks/useSEO';
import apiService, { Product, toThumbSrc, toImageSrc } from '../services/api';
import analytics from '../services/analytics';
import SimpleProductText from '../components/SimpleProductText';
import DiscountBadge from '../components/DiscountBadge';

const palette = {
  background: '#fdf8f2',
  backgroundDeep: '#f4e9de',
  backgroundSoft: '#fbf3ea',
  border: 'rgba(205, 186, 166, 0.55)',
  borderMuted: 'rgba(205, 186, 166, 0.35)',
  card: 'rgba(255, 252, 247, 0.94)',
  accent: '#6b5241',
  accentDeep: '#3e2a1f',
  accentSoft: '#9b7f68',
  text: '#2f2721',
  textMuted: 'rgba(53, 43, 37, 0.68)',
  success: '#4c7661',
  danger: '#b75a4f'
};

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at 12% 18%, rgba(163, 133, 110, 0.18), transparent 46%),
    radial-gradient(circle at 82% 22%, rgba(206, 181, 155, 0.16), transparent 52%),
    radial-gradient(circle at 24% 86%, rgba(167, 143, 122, 0.16), transparent 48%),
    linear-gradient(180deg, ${palette.background} 0%, ${palette.backgroundDeep} 58%, ${palette.backgroundSoft} 100%);
  padding: clamp(96px, 11vw, 140px) 0 clamp(120px, 14vw, 180px);
`;

const HeroBackdrop = styled.section`
  position: relative;
  padding: clamp(32px, 5vw, 48px) 0 clamp(50px, 6vw, 70px);
`;

const HeroInner = styled.div`
  width: min(1120px, 90vw);
  margin: 0 auto;
  display: grid;
  gap: clamp(36px, 5vw, 48px);
`;

const Breadcrumb = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  font-size: 0.92rem;
  color: ${palette.textMuted};
  animation: ${fadeUp} 420ms ease-out 70ms both;

  a {
    color: ${palette.textMuted};
    text-decoration: none;
    padding: 8px 18px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.82);
    border: 1px solid ${palette.borderMuted};
    transition: all 0.22s ease;

    &:hover {
      color: ${palette.accent};
      border-color: rgba(146, 120, 98, 0.55);
      box-shadow: 0 12px 26px rgba(88, 68, 55, 0.14);
    }
  }

  span {
    color: ${palette.accentDeep};
    font-weight: 600;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 46%) minmax(0, 54%);
  gap: clamp(40px, 6vw, 64px);
  align-items: start;
  animation: ${fadeUp} 460ms ease-out 120ms both;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCopy = styled.div`
  display: grid;
  gap: clamp(20px, 3vw, 28px);
`;

const HeroTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 999px;
  font-size: 0.85rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  background: rgba(108, 87, 70, 0.16);
  color: ${palette.accentDeep};
  font-weight: 600;
  width: fit-content;
`;

const HeroTitle = styled.h1`
  font-size: clamp(2.3rem, 3.4vw, 3rem);
  line-height: 1.08;
  color: ${palette.accentDeep};
  margin: 0;
  letter-spacing: -0.01em;
`;

const HeroLead = styled.p`
  font-size: clamp(1.05rem, 1.9vw, 1.22rem);
  line-height: 1.9;
  color: ${palette.textMuted};
  margin: 0;
`;

const HeroHighlightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: clamp(14px, 2.4vw, 20px);
`;

const HeroHighlightCard = styled.div`
  display: grid;
  gap: 6px;
  padding: clamp(16px, 2.4vw, 22px);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(210, 188, 164, 0.35);
  box-shadow: 0 18px 32px rgba(84, 67, 53, 0.12);

  strong {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.95rem;
    color: ${palette.accentDeep};
  }

  span {
    font-size: 0.9rem;
    color: ${palette.textMuted};
    line-height: 1.6;
  }
`;

const PurchaseCard = styled.div`
  position: relative;
  padding: clamp(26px, 3.2vw, 34px);
  border-radius: 30px;
  border: 1px solid ${palette.border};
  background:
    radial-gradient(circle at 16% 16%, rgba(222, 205, 188, 0.25), transparent 58%),
    rgba(255, 252, 247, 0.95);
  box-shadow: 0 32px 70px rgba(62, 44, 31, 0.18);
  display: grid;
  gap: clamp(20px, 3vw, 26px);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 10px;
    border-radius: 24px;
    border: 1px solid rgba(210, 188, 164, 0.35);
    pointer-events: none;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
  flex-wrap: wrap;
`;

const CurrentPrice = styled.span`
  font-size: clamp(1.9rem, 2.9vw, 2.4rem);
  font-weight: 650;
  color: ${palette.accentDeep};
  letter-spacing: -0.015em;
`;

const ComparePrice = styled.span`
  font-size: clamp(1rem, 1.6vw, 1.14rem);
  color: rgba(87, 68, 55, 0.55);
  text-decoration: line-through;
`;

const BadgeStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const CozyPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  background: rgba(111, 90, 69, 0.14);
  color: ${palette.accentDeep};
  font-size: 0.85rem;
  letter-spacing: 0.02em;
`;

const StockStatus = styled.div<{ inStock: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 999px;
  font-size: 0.92rem;
  font-weight: 600;
  background: ${props => (props.inStock ? 'rgba(84, 123, 96, 0.18)' : 'rgba(183, 90, 79, 0.18)')};
  color: ${props => (props.inStock ? palette.success : palette.danger)};

  &::before {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: currentColor;
  }
`;

const QuantitySelector = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  label {
    font-weight: 600;
    color: ${palette.accentDeep};
    font-size: 0.98rem;
  }
`;

const QuantityControls = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${palette.border};
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  overflow: hidden;
`;

const QuantityButton = styled.button`
  width: 44px;
  height: 44px;
  border: none;
  background: transparent;
  color: ${palette.accent};
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(235, 219, 204, 0.56);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 64px;
  height: 44px;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  color: ${palette.accentDeep};

  &:focus {
    outline: none;
  }
`;

const AddToCartButton = styled.button`
  width: 100%;
  padding: 16px 22px;
  border: none;
  border-radius: 18px;
  background: linear-gradient(150deg, ${palette.accentDeep} 0%, #2f1f17 100%);
  color: #fff9f3;
  font-size: 1.08rem;
  font-weight: 630;
  cursor: pointer;
  box-shadow: 0 22px 42px rgba(46, 31, 24, 0.32);
  transition: transform 0.28s ease, box-shadow 0.28s ease, filter 0.28s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 30px 56px rgba(46, 31, 24, 0.36);
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const AssuranceList = styled.ul`
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.9rem;
    color: ${palette.textMuted};
  }
`;

const TrustSection = styled.div`
  margin-top: 1.2rem;
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 1.2rem 1.3rem;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
`;

const TrustGrid = styled.div`
  display: grid;
  gap: 0.9rem;
  margin-bottom: 0.6rem;
`;

const TrustCard = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  font-size: 0.95rem;
  color: ${palette.textMuted};
`;

const TrustIcon = styled.span`
  font-size: 1.3rem;
  line-height: 1;
`;

const TrustCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.95rem;
  color: ${palette.accentDeep};
  font-weight: 600;
  text-decoration: none;

  &::after {
    content: '→';
    transition: transform 0.2s ease;
  }

  &:hover::after {
    transform: translateX(3px);
  }
`;

const HeroGallery = styled.div`
  position: relative;
  display: grid;
  gap: clamp(18px, 3vw, 24px);
`;

const GalleryFrame = styled.div`
  position: relative;
  padding: clamp(20px, 3vw, 28px);
  border-radius: clamp(28px, 4vw, 34px);
  background:
    radial-gradient(circle at 18% 16%, rgba(220, 202, 186, 0.3), transparent 56%),
    radial-gradient(circle at 80% 82%, rgba(202, 183, 164, 0.26), transparent 60%),
    rgba(255, 252, 247, 0.95);
  border: 1px solid ${palette.border};
  box-shadow: 0 36px 70px rgba(63, 47, 33, 0.24);
  display: grid;
  place-items: center;
  min-height: clamp(360px, 44vw, 480px);
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: clamp(16px, 2.6vw, 24px);
    border-radius: clamp(24px, 3vw, 30px);
    border: 1px dashed rgba(198, 172, 148, 0.35);
    pointer-events: none;
  }

  .discount-badge {
    position: absolute;
    top: clamp(18px, 3vw, 26px);
    right: clamp(18px, 3vw, 26px);
    z-index: 3;
  }
`;

const MainImage = styled.img`
  max-height: clamp(320px, 40vw, 440px);
  width: 100%;
  object-fit: contain;
  filter: drop-shadow(0 22px 40px rgba(51, 38, 27, 0.28));
  transition: transform 0.32s ease;

  &:hover {
    transform: scale(1.02);
  }
`;

const ImagePlaceholder = styled.div`
  display: grid;
  place-items: center;
  width: 100%;
  height: clamp(320px, 40vw, 440px);
  border-radius: clamp(24px, 3vw, 28px);
  background: rgba(236, 220, 205, 0.6);
  color: rgba(90, 69, 55, 0.72);
  font-size: clamp(3rem, 8vw, 4.2rem);
  border: 1px dashed rgba(196, 170, 146, 0.7);
`;

const GalleryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(88px, 1fr));
  gap: clamp(12px, 2.4vw, 18px);
`;

const StripThumbnail = styled.button<{ active: boolean }>`
  border: none;
  padding: clamp(8px, 1.6vw, 12px);
  border-radius: 18px;
  background: ${props => (props.active ? 'rgba(245, 228, 210, 0.8)' : 'rgba(255, 255, 255, 0.82)')};
  border: 2px solid ${props => (props.active ? palette.accent : palette.borderMuted)};
  box-shadow: ${props => (props.active ? '0 18px 22px rgba(73, 55, 42, 0.18)' : 'none')};
  cursor: pointer;
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${palette.accent};
  }
`;

const StripImage = styled.img`
  width: 100%;
  height: clamp(72px, 9vw, 92px);
  object-fit: contain;
`;

const StorySection = styled.section`
  padding: clamp(70px, 9vw, 110px) 0 0;
`;

const StoryInner = styled.div`
  width: min(1080px, 88vw);
  margin: 0 auto;
  display: grid;
  gap: clamp(36px, 5vw, 48px);
  animation: ${fadeUp} 520ms ease-out 180ms both;
`;

const StoryHeader = styled.div`
  max-width: 720px;
  display: grid;
  gap: 12px;

  h2 {
    font-size: clamp(1.7rem, 2.6vw, 2.2rem);
    color: ${palette.accentDeep};
    margin: 0;
    letter-spacing: -0.01em;
  }

  p {
    font-size: clamp(1rem, 1.8vw, 1.12rem);
    color: ${palette.textMuted};
    margin: 0;
    line-height: 1.86;
  }
`;

const StoryLayout = styled.div`
  display: grid;
  gap: clamp(24px, 4vw, 34px);
  grid-template-columns: minmax(0, 2.2fr) minmax(0, 1.1fr);

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const NarrativeCard = styled.div`
  padding: clamp(26px, 3.2vw, 32px);
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid ${palette.border};
  box-shadow: 0 28px 56px rgba(74, 58, 46, 0.16);
  color: ${palette.textMuted};
  line-height: 1.86;

  .simple-product-text {
    font-size: clamp(1.02rem, 1.8vw, 1.14rem);

    p {
      margin-bottom: 16px;
    }
  }
`;

const DetailsCard = styled.div`
  padding: clamp(22px, 3vw, 28px);
  border-radius: 28px;
  background:
    radial-gradient(circle at 10% 18%, rgba(220, 202, 186, 0.24), transparent 60%),
    rgba(255, 252, 247, 0.94);
  border: 1px solid ${palette.border};
  box-shadow: 0 26px 48px rgba(74, 58, 46, 0.14);
  display: grid;
  gap: 18px;
`;

const DetailItem = styled.div`
  display: grid;
  gap: 6px;

  span {
    font-size: 0.78rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(90, 72, 58, 0.66);
    font-weight: 600;
  }

  strong {
    font-size: 1rem;
    color: ${palette.accentDeep};
  }
`;

const ExperienceSection = styled.div`
  display: grid;
  gap: clamp(20px, 3vw, 28px);
`;

const ExperienceHeading = styled.h3`
  font-size: clamp(1.28rem, 2vw, 1.6rem);
  color: ${palette.accentDeep};
  margin: 0;
`;

const ExperienceIntro = styled.p`
  font-size: 0.98rem;
  color: ${palette.textMuted};
  margin: 0;
  line-height: 1.7;
`;

const ExperienceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: clamp(18px, 3vw, 26px);
`;

const ExperienceCard = styled.div`
  padding: clamp(18px, 2.6vw, 24px);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(213, 195, 176, 0.45);
  box-shadow: 0 22px 40px rgba(68, 52, 39, 0.14);
  display: grid;
  gap: 10px;

  strong {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 1rem;
    color: ${palette.accentDeep};
  }

  span {
    font-size: 0.92rem;
    color: ${palette.textMuted};
    line-height: 1.7;
  }
`;

const RelatedSection = styled.section`
  padding: clamp(70px, 9vw, 110px) 0;

  .container {
    width: min(1120px, 90vw);
    margin: 0 auto;
  }
`;

const RelatedHeader = styled.div`
  display: grid;
  gap: 10px;
  margin-bottom: var(--spacing-8);

  h2 {
    font-size: clamp(1.7rem, 2.6vw, 2.2rem);
    color: ${palette.accentDeep};
    margin: 0;
  }

  p {
    font-size: 0.98rem;
    color: ${palette.textMuted};
    margin: 0;
    max-width: 620px;
    line-height: 1.7;
  }
`;

const RelatedGrid = styled.div`
  display: grid;
  gap: clamp(18px, 3vw, 26px);
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const RelatedCard = styled(Link)`
  display: grid;
  gap: 10px;
  background: #ffffff;
  border-radius: 22px;
  border: 1px solid ${palette.borderMuted};
  box-shadow: 0 18px 34px rgba(64, 48, 34, 0.14);
  padding: 14px 16px 16px 16px;
  text-decoration: none;
  color: inherit;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 26px 48px rgba(64, 48, 34, 0.2);
  }
`;

const RelatedImage = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 16px;
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const RelatedName = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${palette.accentDeep};
  margin: 0;
`;

const RelatedPrice = styled.div`
  font-size: 0.95rem;
  color: ${palette.accentDeep};
  font-weight: 600;
`;

const LoadingWrapper = styled.div`
  display: grid;
  place-items: center;
  min-height: 50vh;
  color: ${palette.accent};
  font-size: 1.08rem;
  letter-spacing: 0.02em;

  &::before {
    content: '⏳';
    font-size: 3rem;
    margin-bottom: 10px;
  }
`;

const ErrorWrapper = styled.div`
  display: grid;
  gap: 20px;
  justify-items: center;
  text-align: center;
  padding: clamp(60px, 7vw, 80px) clamp(20px, 6vw, 40px);
  background: rgba(255, 255, 255, 0.9);
  border-radius: 32px;
  border: 1px solid ${palette.border};
  box-shadow: 0 28px 56px rgba(66, 50, 38, 0.18);

  h2 {
    color: ${palette.accentDeep};
    margin: 0;
  }

  p {
    color: ${palette.textMuted};
    margin: 0;
    max-width: 420px;
    line-height: 1.7;
  }
`;

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addItem } = useCart();
  const { showToast } = useToast();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);

  // Use SEO hook for dynamic metadata
  useSEO({
    entityType: 'product',
    entityId: product?.id,
    fallbackTitle: product ? `${product.name} - Lillaflora` : 'Produkt - Lillaflora',
    fallbackDescription: product?.short_description || product?.description || 'Upptäck våra produkter på Lillaflora',
    fallbackImage: product?.images?.[0] || '/static/images/default-product.jpg',
    entityData: product
  });

  const getProductEmoji = (categories: string[] = []) => {
    if (categories.some(cat => cat.toLowerCase().includes('ljus'))) return '🕯️';
    if (categories.some(cat => cat.toLowerCase().includes('godis'))) return '🍬';
    if (categories.some(cat => cat.toLowerCase().includes('cottage'))) return '☕';
    return '🏺';
  };

  useEffect(() => {
    if (!id) return;

    let isActive = true;

    const loadProduct = async () => {
      setLoading(true);
      setError(false);

      try {
        const data = await apiService.getProduct(id);
        if (!isActive) return;

        setProduct(data);

        if (data?.id && data?.name) {
          analytics.trackPageView(data.name, data.id);
          analytics.trackProductView(data.id, data.name);

          // Load related products based on this product
          try {
            const related = await apiService.getRelatedProducts(data.id, 4);
            setRelatedProducts(related || []);
          } catch (relatedError) {
            console.error('Error fetching related products:', relatedError);
            setRelatedProducts([]);
          }
        }
      } catch (error) {
        if (!isActive) return;
        console.error('Error fetching product:', error);
        setError(true);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadProduct();

    return () => {
      isActive = false;
    };
  }, [id]);

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= (product?.inventory_quantity || 0)) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (!product || !product.id) return;
    
    addItem(product.id, quantity);
    
    // Track add to cart event
    analytics.trackAddToCart(product.id, product.name, quantity, Number(product.price));
    
    showToast(`${product.name} har lagts till i varukorgen! 🛒`, { type: 'success' });
  };

  const formatPrice = (price: number) => {
    return `${price.toFixed(0)} kr`;
  };

  const inStock = (product?.inventory_quantity ?? 0) > 0;
  const images = product?.images && product.images.length > 0 ? product.images : [];
  const categoriesText = product?.categories?.join(', ') || 'Ingen kategori';
  const primaryCategorySlug = product?.category_slugs && product.category_slugs.length > 0
    ? product.category_slugs[0]
    : undefined;
  const heroTagLabel = product?.categories?.[0] || 'Lilla Flora-favorit';
  const tagline =
    (product?.short_description && product.short_description.trim().length > 0
      ? product.short_description.trim()
      : null) ||
    (product?.categories && product.categories.length > 0
      ? `Tillhör ${product.categories.join(' • ')}`
      : 'Utvald för att skapa värme och ro i hemmet.');

  const heroHighlights = useMemo(
    () => [
      {
        icon: '🏷️',
        title: 'Kategori',
        description: categoriesText
      },
      {
        icon: inStock ? '🌿' : '🕰️',
        title: inStock ? 'I lager' : 'Tillgänglighet',
        description: inStock
          ? `${product?.inventory_quantity ?? 0} st redo för leverans`
          : 'Slutsåld just nu – hör av dig så ordnar vi nästa leverans.'
      },
      {
        icon: '📍',
        title: 'Butik & leverans',
        description: 'Hämta i butiken i Kramfors eller välj lokal utkörning.'
      }
    ],
    [categoriesText, inStock, product?.inventory_quantity]
  );

  const experienceHighlights = useMemo(() => {
    const normalized = categoriesText.toLowerCase();

    const base = [
      {
        icon: '🌟',
        title: 'Stämning i hemmet',
        description: 'Skapar en mjuk, inbjudande känsla i vardagsrum och sovrum.'
      },
      {
        icon: '🧺',
        title: 'Matcha med',
        description: 'Kombinera med naturmaterial, linne och varma metaller för en harmonisk helhet.'
      },
      {
        icon: '🏡',
        title: 'Skapa din ritual',
        description: 'Lyft vardagens små stunder – kombinera med dina favoritdetaljer för ett tryggt hem.'
      }
    ];

    if (normalized.match(/ljus|doft/)) {
      base[0] = {
        icon: '🕯️',
        title: 'Doft & ljus',
        description: 'Fyller rummet med mjuk belysning och subtila doftnoter för kvällsro.'
      };
    } else if (normalized.match(/textil|pläd|kudde/)) {
      base[0] = {
        icon: '🪄',
        title: 'Mjuk textur',
        description: 'Ger soffan eller favoritfåtöljen ett omfamnande lager av mjukhet.'
      };
    } else if (normalized.match(/konst|blomm/)) {
      base[0] = {
        icon: '🌸',
        title: 'Levande uttryck',
        description: 'För in naturen i hemmet med hållbara, säsongslösa detaljer.'
      };
    }

    if (normalized.match(/kök|dukning|mugg|kopp/)) {
      base[1] = {
        icon: '☕',
        title: 'Gör plats vid bordet',
        description: 'Perfekt till en lugn frukost, eftermiddagsfika eller dukning med nära och kära.'
      };
    }

    return base;
  }, [categoriesText]);

  const assuranceList = useMemo(
    () => [
      {
        icon: '✨',
        text: 'Personligt utvald av vårt team i butiken – alltid omsorgsfull kvalitet.'
      },
      {
        icon: '📝',
        text: 'Vi skickar gärna med en handskriven hälsning till din beställning.'
      },
      {
        icon: '🚚',
        text: 'Snabb lokal leverans eller smidig upphämtning i vår butik i Kramfors.'
      }
    ],
    []
  );

  if (loading) {
    return (
      <PageWrapper>
        <LoadingWrapper>Laddar produkt...</LoadingWrapper>
      </PageWrapper>
    );
  }

  if (error || !product) {
    return (
      <PageWrapper>
        <ErrorWrapper>
          <h2>Produkten kunde inte hittas</h2>
          <p>
            Tyvärr kunde vi inte hitta produkten du letar efter. Den kanske har tagits bort eller så är länken felaktig.
          </p>
          <Link
            to="/produkter"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: palette.accentDeep,
              color: '#fffdf6',
              textDecoration: 'none',
              borderRadius: '999px',
              fontWeight: 600
            }}
          >
            Tillbaka till produkter
          </Link>
        </ErrorWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <HeroBackdrop>
        <HeroInner>
          <Breadcrumb>
            <Link to="/">Hem</Link>
            <Link to="/produkter">Produkter</Link>
            <span>{product.name}</span>
          </Breadcrumb>

          <HeroGrid>
            <HeroCopy>
              {heroTagLabel && <HeroTag>{heroTagLabel}</HeroTag>}
              <HeroTitle>{product.name}</HeroTitle>
              {tagline && <HeroLead>{tagline}</HeroLead>}

              <HeroHighlightGrid>
                {heroHighlights.map((item, index) => (
                  <HeroHighlightCard key={`${item.title}-${index}`}>
                    <strong>
                      <span aria-hidden="true">{item.icon}</span>
                      {item.title}
                    </strong>
                    <span>{item.description}</span>
                  </HeroHighlightCard>
                ))}
              </HeroHighlightGrid>

              <PurchaseCard>
                <PriceRow>
                  {product.has_active_discount ? (
                    <>
                      <CurrentPrice>
                        {formatPrice(product.final_price ? product.final_price : product.price)}
                      </CurrentPrice>
                      <ComparePrice>{formatPrice(product.price)}</ComparePrice>
                    </>
                  ) : (
                    <>
                      <CurrentPrice>{formatPrice(product.price)}</CurrentPrice>
                      {product.compare_price && product.compare_price > product.price && (
                        <ComparePrice>{formatPrice(product.compare_price)}</ComparePrice>
                      )}
                    </>
                  )}
                </PriceRow>

                <BadgeStack>
                  {product.has_active_discount && product.discount_type && (
                    <DiscountBadge
                      discountPercentage={product.discount_percentage}
                      discountAmount={product.discount_amount}
                      discountType={product.discount_type}
                      variant="inline"
                    />
                  )}
                  {product.sku && <CozyPill>Artikelnummer {product.sku}</CozyPill>}
                  <CozyPill>{categoriesText}</CozyPill>
                </BadgeStack>

                <StockStatus inStock={inStock}>
                  {inStock ? `${product.inventory_quantity} i lager` : 'Slutsåld – kontakta oss gärna'}
                </StockStatus>

                {inStock ? (
                  <>
                    <QuantitySelector>
                      <label>Antal:</label>
                      <QuantityControls>
                        <QuantityButton
                          type="button"
                          onClick={() => handleQuantityChange(quantity - 1)}
                          disabled={quantity <= 1}
                          aria-label="Minska antal"
                        >
                          −
                        </QuantityButton>
                        <QuantityInput
                          type="number"
                          min="1"
                          max={product.inventory_quantity}
                          value={quantity}
                          onChange={e => handleQuantityChange(parseInt(e.target.value, 10) || 1)}
                          aria-label="Antal"
                        />
                        <QuantityButton
                          type="button"
                          onClick={() => handleQuantityChange(quantity + 1)}
                          disabled={quantity >= (product.inventory_quantity || 0)}
                          aria-label="Öka antal"
                        >
                          +
                        </QuantityButton>
                      </QuantityControls>
                    </QuantitySelector>

                    <AddToCartButton onClick={handleAddToCart}>Lägg i varukorg 🛒</AddToCartButton>
                  </>
                ) : (
                  <AddToCartButton disabled>Slutsåld – kontakta butiken</AddToCartButton>
                )}

                <AssuranceList>
                  {assuranceList.map((item, index) => (
                    <li key={`${item.icon}-${index}`}>
                      <span aria-hidden="true">{item.icon}</span>
                      <span>{item.text}</span>
                    </li>
                  ))}
                </AssuranceList>
                <TrustSection>
                  <TrustGrid>
                    <TrustCard>
                      <TrustIcon>📍</TrustIcon>
                      <div>
                        Lokalt i Kramfors – vi är en fysisk blomsterbutik med öppet köp och personlig service.
                      </div>
                    </TrustCard>
                    <TrustCard>
                      <TrustIcon>🚚</TrustIcon>
                      <div>
                        Snabb leverans 1‑3 arbetsdagar i hela Sverige, spåra paket via mejlet efter order.
                      </div>
                    </TrustCard>
                    <TrustCard>
                      <TrustIcon>🔒</TrustIcon>
                      <div>
                        Trygga betalningar, fri frakt över 500 kr och tydliga returer inom 14 dagar.
                      </div>
                    </TrustCard>
                  </TrustGrid>
                  <TrustCTA to="/kontakt">Behöver du hjälp? Kontakta oss →</TrustCTA>
                </TrustSection>
              </PurchaseCard>
            </HeroCopy>

            <HeroGallery>
              <GalleryFrame>
                {images.length > 0 ? (
                  <MainImage
                    src={toImageSrc(images[currentIndex])}
                    alt={product.name}
                    onError={e => {
                      e.currentTarget.style.display = 'none';
                      const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (nextElement) {
                        nextElement.style.display = 'grid';
                      }
                    }}
                  />
                ) : (
                  <ImagePlaceholder>{getProductEmoji(product.categories)}</ImagePlaceholder>
                )}

                {product.has_active_discount && product.discount_type && (
                  <div className="discount-badge">
                    <DiscountBadge
                      discountPercentage={product.discount_percentage}
                      discountAmount={product.discount_amount}
                      discountType={product.discount_type}
                      discountLabel={product.discount_label}
                      size="large"
                      showLabel={!!product.discount_label}
                    />
                  </div>
                )}
              </GalleryFrame>

              {images.length > 1 && (
                <GalleryStrip>
                  {images.map((image, index) => (
                    <StripThumbnail
                      key={`${image}-${index}`}
                      type="button"
                      active={index === currentIndex}
                      onClick={() => setCurrentIndex(index)}
                      aria-label={`Visa bild ${index + 1}`}
                    >
                      <StripImage src={toImageSrc(image)} alt={`${product.name} ${index + 1}`} />
                    </StripThumbnail>
                  ))}
                </GalleryStrip>
              )}
            </HeroGallery>
          </HeroGrid>
        </HeroInner>
      </HeroBackdrop>

      <StorySection>
        <StoryInner>
          <StoryHeader>
            <h2>Produktberättelse</h2>
            <p>
              {product.short_description
                ? product.short_description
                : 'Vårda stämningen hemma med noggrant utvald kvalitet och en varm, inbjudande känsla.'}
            </p>
          </StoryHeader>

          <StoryLayout>
            <NarrativeCard>
              <SimpleProductText text={product.description} />
            </NarrativeCard>

            <DetailsCard>
              <DetailItem>
                <span>Artikelnummer</span>
                <strong>{product.sku}</strong>
              </DetailItem>
              <DetailItem>
                <span>Kategori</span>
                <strong>
                  {categoriesText}
                  {primaryCategorySlug && (
                    <>
                      {' · '}
                      <Link to={`/produkter?kategori=${primaryCategorySlug}`}>
                        Visa fler {heroTagLabel.toLowerCase()}
                      </Link>
                    </>
                  )}
                </strong>
              </DetailItem>
              <DetailItem>
                <span>Pris</span>
                <strong>
                  {product.has_active_discount && product.final_price
                    ? formatPrice(product.final_price)
                    : formatPrice(product.price)}
                </strong>
              </DetailItem>
              <DetailItem>
                <span>Lagersaldo</span>
                <strong>{inStock ? `${product.inventory_quantity} st` : 'Slutsåld'}</strong>
              </DetailItem>
            </DetailsCard>
          </StoryLayout>

          <ExperienceSection>
            <ExperienceHeading>Så skapar du känslan hemma</ExperienceHeading>
            <ExperienceIntro>
              {tagline ||
                'Små detaljer gör stor skillnad – låt produkten bli en varm hälsning till dig själv eller någon du tycker om.'}
            </ExperienceIntro>
          <ExperienceGrid>
            {experienceHighlights.map((item, index) => (
              <ExperienceCard key={`${item.icon}-${index}`}>
                <strong>
                  <span aria-hidden="true">{item.icon}</span>
                  {item.title}
                </strong>
                <span>{item.description}</span>
              </ExperienceCard>
            ))}
          </ExperienceGrid>
        </ExperienceSection>
      </StoryInner>
      </StorySection>

      {relatedProducts.length > 0 && (
        <RelatedSection>
          <div className="container">
            <RelatedHeader>
              <h2>Passar bra tillsammans med …</h2>
              <p>
                Här är några produkter som ofta köps tillsammans med{' '}
                <strong>{product.name}</strong> och som kompletterar känslan i samma rum.
              </p>
            </RelatedHeader>

            <RelatedGrid>
              {relatedProducts.map((related) => (
                <RelatedCard key={related.id} to={`/produkter/${related.slug || related.id}`}>
                  <RelatedImage>
                    {related.images && related.images.length > 0 ? (
                      <img
                        src={toThumbSrc(related.images[0])}
                        alt={related.name}
                        loading="lazy"
                      />
                    ) : (
                      <span aria-hidden="true">🛍️</span>
                    )}
                  </RelatedImage>
                  <RelatedName>{related.name}</RelatedName>
                  <RelatedPrice>
                    {formatPrice(related.final_price ?? related.price)}
                  </RelatedPrice>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </div>
        </RelatedSection>
      )}
    </PageWrapper>
  );
};

export default ProductDetail;
